import { useState, ReactNode } from "react";
import { Config } from "../config/Config";
import { ChatDataProps } from "../types/backend/SengiLabsTypes";
import { normalizeLLMResponse, parseResponseToChatDataProps } from "../utils/chat/chatPropsParsing";
import { aiStateTypes } from "../types/ai/states";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { useTrackedRun } from "./useTrackedRun";

export interface AiLLMProps {
    model:string;
    messages: {role: 'assistant' | 'user', content: string;}[];
};

export const useAiLLM = () : [
    generation:any,
    state: aiStateTypes,
    setState: (state:aiStateTypes) => void,
    sendMessage: (path:string,body:AiLLMProps, returnResponseOnly?: boolean) => void,
    insufficientCreditsDialog: ReactNode
] => {
    const [state, setState] = useState<aiStateTypes>(null)
    const baseUrl = Config.json.AiServiceProxyUrl;
    const [generation, setGeneration] = useState<any>([]);
    const { startRun, completeRun, failRun, insufficientCreditsDialog } = useTrackedRun();

    const sendMessage = async(path:string,body:AiLLMProps, returnResponseOnly: boolean=false) =>{
        setState('waiting');

        const runHandle = await startRun('llm', { estimatedCost: 0, model: body.model });
        if (!runHandle) {
            // Either insufficient balance (dialog shown by useTrackedRun) or another failure -
            // fail closed, don't call cProxy without a tracked run.
            setState('error');
            setGeneration(null);
            return;
        }

        try {
            const res = await fetchWithAuth(`${baseUrl}/${path}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body)
            });

            if (!res || !res.ok) {
                failRun(runHandle, `LLM request failed (HTTP ${res?.status})`);
                setState('error');
                setGeneration(null);
                return;
            };
            const data = await res.json();
            const usage = data.raw?.usage;
            completeRun(runHandle, {
                inputTokens: usage?.prompt_tokens ?? 0,
                outputTokens: usage?.completion_tokens ?? 0,
            });

            const reply = data.reply || "No response.";
            if(returnResponseOnly){
                const normalizedReply = normalizeLLMResponse(reply);
                setGeneration(normalizedReply);
            }
            else{
                const parsedReply: ChatDataProps[] = parseResponseToChatDataProps(reply);
                setGeneration(parsedReply);
            }
            setState('done');
        } catch (err:any) {
            failRun(runHandle, `LLM request error: ${err}`);
            setState('error');
            setGeneration(null);
            return;;
        }
    };

    return [generation,state,setState,sendMessage,insufficientCreditsDialog];
};

